import { useAccessStatus, TierEntitlements, SubscriptionTier } from './useAccessStatus';

/**
 * Hook for checking tier-based feature access.
 * 
 * Usage:
 * const { canUseGamification, canUseDifficultyLevels, tier, isLoading } = useTierAccess();
 * 
 * Feature access by tier:
 * - Basic ($49): Core training, transcripts, scorecards, rep dashboard
 * - Pro ($79): + Gamification, Leaderboards, Difficulty levels, Manager dashboard
 * - Enterprise ($99): + Roll-up analytics, CRM integrations
 * - Founder: Lifetime Pro access
 */
export function useTierAccess() {
  const { isLoading, accessStatus, tier, entitlements, error } = useAccessStatus();

  // Convenience boolean for subscription status
  const hasActiveSubscription = accessStatus?.allowed ?? false;
  
  // Founder status
  const isFounder = accessStatus?.isFounder ?? false;

  return {
    // Loading state
    isLoading,
    error,

    // Subscription info
    hasActiveSubscription,
    tier,
    tierDisplayName: getTierDisplayName(tier, isFounder),
    isFounder,

    // All entitlements spread for easy access
    ...entitlements,

    // Raw entitlements object if needed
    entitlements,

    // Helper to check if feature is locked
    isFeatureLocked: (feature: keyof TierEntitlements) => !entitlements[feature],

    // Helper to get required tier for a feature
    getRequiredTier: (feature: keyof TierEntitlements) => getRequiredTierForFeature(feature),
  };
}

function getTierDisplayName(tier: SubscriptionTier, isFounder?: boolean): string {
  if (isFounder) {
    return 'Pro (Founder)';
  }
  
  switch (tier) {
    case 'basic':
      return 'Basic';
    case 'pro':
      return 'Pro';
    case 'enterprise':
      return 'Enterprise';
    default:
      return 'Free';
  }
}

function getRequiredTierForFeature(feature: keyof TierEntitlements): 'pro' | 'enterprise' {
  // Enterprise-only features
  if (feature === 'canUseRollupAnalytics' || feature === 'canUseCrmIntegrations') {
    return 'enterprise';
  }
  // All other gated features require Pro
  return 'pro';
}

// Export types for use in components
export type { TierEntitlements, SubscriptionTier } from './useAccessStatus';

